import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import {
  BxButtonComponent,
  BxFeaturedIconOutlineComponent,
  alertCircleIcon,
  injectBxIcons,
  lock01Icon,
  searchLgIcon,
} from '@beyonnex/bx-angular-ui';
import type { BxFeaturedIconColor, BxIcon } from '@beyonnex/bx-angular-ui';
import { TranslatePipe } from '@ngx-translate/core';
import type { IOverlayAngularComp } from 'ag-grid-angular';
import type { IOverlayParams } from 'ag-grid-community';
import type { PropertyOverviewOverlayInputs } from '../property-overview.models';

/** AG Grid merges its own params into whatever `activeOverlayParams` supplies, so the overlay sees both. */
type OverlayParams = IOverlayParams & PropertyOverviewOverlayInputs;

// Fills the grid body whenever there is nothing to show, so a failed request never reads as an empty one.
@Component({
  selector: 'bxi-property-overview-overlay',
  imports: [BxButtonComponent, BxFeaturedIconOutlineComponent, TranslatePipe],
  templateUrl: './property-overview-overlay.component.html',
  // `.ag-overlay` sets pointer-events: none, and only AG Grid's own loading and modal wrappers turn them
  // back on — without this the retry button renders but never receives a click.
  host: { class: 'pointer-events-auto block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyOverviewOverlayComponent implements IOverlayAngularComp {
  constructor() {
    injectBxIcons([searchLgIcon, lock01Icon, alertCircleIcon]);
  }

  private readonly params = signal<OverlayParams | undefined>(undefined);

  agInit(params: OverlayParams): void {
    this.params.set(params);
  }

  // AG Grid refreshes the live instance on an `activeOverlayParams` change rather than re-running `agInit`.
  refresh(params: OverlayParams): void {
    this.params.set(params);
  }

  protected readonly variant = computed<OverlayVariant | undefined>(() => {
    const params = this.params();
    if (!params) {
      return undefined;
    }

    const { outcome, filtered } = params;
    if (outcome.status === 'failed') {
      return OVERLAY_VARIANT[outcome.denied ? 'denied' : 'failed'];
    }

    // An active filter is the likeliest reason the list came back empty, so the wording says so.
    return filtered ? { ...OVERLAY_VARIANT.empty, message: EMPTY_FILTERED_MESSAGE } : OVERLAY_VARIANT.empty;
  });

  /** Retrying a denied request cannot succeed, so only the generic failure offers it. */
  protected readonly showsRetry = computed(() => {
    const outcome = this.params()?.outcome;

    return outcome?.status === 'failed' && !outcome.denied;
  });

  protected handleRetryClick(): void {
    this.params()?.retry();
  }
}

type OverlayKind = 'empty' | 'denied' | 'failed';

interface OverlayVariant {
  icon: BxIcon;
  color: BxFeaturedIconColor;
  title: string;
  message: string;
}

const OVERLAY_VARIANT: Record<OverlayKind, OverlayVariant> = {
  empty: {
    icon: 'search-lg',
    color: 'gray',
    title: 'product-view.property-overview.empty.title',
    message: 'product-view.property-overview.empty.message',
  },
  denied: {
    icon: 'lock-01',
    color: 'warning',
    title: 'product-view.property-overview.error.denied.title',
    message: 'product-view.property-overview.error.denied.message',
  },
  failed: {
    icon: 'alert-circle',
    color: 'error',
    title: 'product-view.property-overview.error.failed.title',
    message: 'product-view.property-overview.error.failed.message',
  },
};

const EMPTY_FILTERED_MESSAGE = 'product-view.property-overview.empty.filteredMessage';
